"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { Clock, BookOpen, ChevronLeft, ChevronRight, Star, Users, Check } from "lucide-react";

interface Course {
  course_id: number;
  course_key: string;
  course_slug: string;
  course_title: string;
  course_description: string;
  course_workload: number;
  course_level: string;
  course_price_presencial: number;
  course_price_ead: number;
  course_price_installments_ead: number;
  course_installments_ead: number;
  course_banner_small: string | null;
  course_banner_large: string | null;
  course_total_rating: number;
  course_total_reviews: number;
  total_lessons: number;
  total_students: number;
}

function StarRating({ rating, reviewCount }: { rating: number; reviewCount: number }) {
  const numRating = Number(rating) || 0;
  const numReviewCount = Number(reviewCount) || 0;

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              star <= numRating ? "fill-amber-400 text-amber-400" : "fill-gray-700 text-gray-700"
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-medium text-white">{numRating.toFixed(1)}</span>
      <span className="text-xs text-gray-500">({numReviewCount})</span>
    </div>
  );
}

function CourseCard({ course }: { course: Course }) {
  const [isHovered, setIsHovered] = useState(false);

  const getLevelLabel = (level: string) => {
    switch (level) {
      case "basico":
        return "Básico";
      case "intermediario":
        return "Intermediário";
      case "avancado":
        return "Avançado";
      default:
        return level;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "basico":
        return "bg-emerald-500";
      case "intermediario":
        return "bg-amber-500";
      case "avancado":
        return "bg-rose-500";
      default:
        return "bg-[#2AABDE]";
    }
  };

  const formatWorkload = (seconds: number) => {
    const totalMinutes = Math.floor(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hours === 0) {
      return `${mins}min`;
    }
    if (mins === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${mins}min`;
  };

  const price = Number(course.course_price_ead) || Number(course.course_price_presencial) || 0;
  const installmentTotal = Number(course.course_price_installments_ead) || 0;
  const installmentCount = Number(course.course_installments_ead) || 1;
  const installmentValue = installmentTotal > 0 && installmentCount > 1 ? installmentTotal / installmentCount : 0;

  // Extrair tópicos da descrição (cada linha é um tópico)
  const topics = course.course_description
    ? course.course_description.split('\n').filter(line => line.trim()).slice(0, 3)
    : [];

  return (
    <div
      className="relative flex-shrink-0 w-[320px]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main Card */}
      <div className="group bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-gray-900/50 hover:-translate-y-2 border border-gray-700">
        {/* Image Container */}
        <div className="relative h-[180px] overflow-hidden">
          {/* Image */}
          <Image
            src={course.course_banner_large || course.course_banner_small || "/data-client/course-placeholder.jpg"}
            alt={course.course_title}
            fill
            className={`object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`}
            unoptimized
          />

          {/* Bottom Info on Image */}
          <div className="absolute bottom-3 left-3 right-3 z-20 flex items-center justify-between">
            <span className={`${getLevelColor(course.course_level)} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
              {getLevelLabel(course.course_level)}
            </span>
            <div className="flex items-center gap-1 text-white/80 text-xs bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full">
              <Users className="w-3.5 h-3.5" />
              <span>{course.total_students || 0} alunos</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Title */}
          <h3 className="font-bold text-white text-base mb-3 line-clamp-2 min-h-[48px] group-hover:text-gray-300 transition-colors duration-300">
            {course.course_title}
          </h3>

          {/* Rating */}
          <div className="mb-4">
            <StarRating rating={course.course_total_rating || 0} reviewCount={course.course_total_reviews || 0} />
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-4" />

          {/* Stats */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5 text-gray-400">
              <div className="w-8 h-8 rounded-lg bg-gray-700/50 flex items-center justify-center">
                <Clock className="w-4 h-4 text-gray-400" />
              </div>
              <span>{formatWorkload(course.course_workload || 0)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-400">
              <div className="w-8 h-8 rounded-lg bg-gray-700/50 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-gray-400" />
              </div>
              <span>{course.total_lessons || 0} Aulas</span>
            </div>
          </div>

          {/* Price and CTA Button */}
          <div className="mt-5">
            {price > 0 && (
              <div className="text-left mb-3">
                <span className="text-2xl font-bold text-emerald-500">R$ {price.toFixed(2)}</span>
                {installmentValue > 0 && (
                  <p className="text-sm text-gray-400 mt-1">
                    Ou em até {installmentCount}x de R$ {installmentValue.toFixed(2)} no cartão
                  </p>
                )}
              </div>
            )}
            <button className="w-full py-3 bg-gray-700 text-white font-semibold rounded-xl transition-all duration-300 hover:bg-gray-600 hover:shadow-lg hover:shadow-gray-900/40 active:scale-[0.98]">
              Saiba Mais
            </button>
          </div>
        </div>
      </div>

      {/* Hover Popup */}
      <div
        className={`absolute left-full top-0 ml-4 w-[340px] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 transition-all duration-300 ${
          isHovered ? "opacity-100 translate-x-0 pointer-events-auto" : "opacity-0 -translate-x-4 pointer-events-none"
        }`}
      >
        {/* Arrow */}
        <div className="absolute left-0 top-8 -translate-x-2 w-4 h-4 bg-white border-l border-b border-gray-200 rotate-45" />

        <div className="p-6">
          {/* Title */}
          <h4 className="font-bold text-gray-900 text-lg mb-3 leading-tight">
            {course.course_title}
          </h4>

          {/* Badges */}
          <div className="flex items-center gap-2 mb-4">
            <span className={`${getLevelColor(course.course_level)} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
              {getLevelLabel(course.course_level)}
            </span>
            <span className="text-xs text-gray-500">
              Atualizado recentemente
            </span>
          </div>

          {/* Info Line */}
          <p className="text-sm text-gray-600 mb-4">
            {formatWorkload(course.course_workload || 0)} no total · {course.total_lessons || 0} aulas · {getLevelLabel(course.course_level)}
          </p>

          {/* Description */}
          {course.course_description && (
            <p className="text-sm text-gray-700 mb-4 line-clamp-3">
              {course.course_description}
            </p>
          )}

          {/* Topics */}
          {topics.length > 0 && (
            <ul className="space-y-2 mb-5">
              {topics.map((topic, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <span className="line-clamp-2">{topic}</span>
                </li>
              ))}
            </ul>
          )}

          {/* CTA Button */}
          <button className="w-full py-3 bg-[#2AABDE] text-white font-semibold rounded-lg transition-all duration-300 hover:bg-[#1a8ab8] hover:shadow-lg">
            Saiba Mais
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FeaturedCourses() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:5000";
        const response = await fetch(`${backendUrl}/api/website/public/courses/featured`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setCourses(data.data || []);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar cursos em destaque:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 340;
      const newScrollLeft =
        direction === "left"
          ? scrollContainerRef.current.scrollLeft - scrollAmount
          : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });

      setTimeout(checkScrollButtons, 300);
    }
  };

  // Não renderiza a seção se não houver cursos
  if (!isLoading && courses.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-[1400px] mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-[#2AABDE] font-semibold text-sm uppercase tracking-wider mb-2">
            Aprenda com os melhores
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Cursos em <span className="text-[#2AABDE]">Destaque</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Escolha entre nossos cursos mais populares e comece sua jornada profissional na área offshore
          </p>
          <div className="w-24 h-1.5 bg-gradient-to-r from-[#2AABDE] to-[#1a8ab8] mx-auto rounded-full mt-6" />
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2AABDE]"></div>
          </div>
        ) : (
          <>
            {/* Carousel Container */}
            <div className="relative">
              {/* Left Arrow */}
              <button
                onClick={() => scroll("left")}
                disabled={!canScrollLeft}
                className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-14 h-14 bg-white rounded-full shadow-xl flex items-center justify-center transition-all duration-300 border border-gray-100 ${
                  canScrollLeft
                    ? "opacity-100 hover:bg-[#2AABDE] hover:text-white hover:border-[#2AABDE] cursor-pointer"
                    : "opacity-0 cursor-default"
                }`}
                aria-label="Anterior"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Cards Container */}
              <div
                ref={scrollContainerRef}
                onScroll={checkScrollButtons}
                className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-6 px-2"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {courses.map((course) => (
                  <Link href={`/cursos/${course.course_slug}`} key={course.course_id}>
                    <CourseCard course={course} />
                  </Link>
                ))}
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => scroll("right")}
                disabled={!canScrollRight}
                className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-14 h-14 bg-white rounded-full shadow-xl flex items-center justify-center transition-all duration-300 border border-gray-100 ${
                  canScrollRight
                    ? "opacity-100 hover:bg-[#2AABDE] hover:text-white hover:border-[#2AABDE] cursor-pointer"
                    : "opacity-0 cursor-default"
                }`}
                aria-label="Próximo"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* View All Link */}
            <div className="text-center mt-10">
              <Link
                href="/cursos"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                Ver todos os cursos
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
